import { UISchema } from '@/type';
import { MaterialEditorConfig, TextureSlotSelect } from '../type';

export function getStandardMaterialSchema(config: MaterialEditorConfig): UISchema {
  const mapEnable = config.textureSlots.map?.config?.enable;
  return [
    {
      uiType: 'InputText',
      fieldName: 'type',
      label: '材质类型',
      disabled: true,
    },
    // 基础纹理和颜色
    {
      uiType: 'ColorPicker',
      fieldName: 'color',
      label: '基础颜色',
      type: ['pure'],
      tooltip: '控制材质的基本颜色，决定物体表面的主色调。',
    },
    {
      uiType: 'Select',
      fieldName: 'textureSlots.map.value',
      label: '基础贴图',
      options: (config.textureSlots.map as TextureSlotSelect).options,
      allowClear: true,
    },
    {
      uiType: 'Group',
      showConnectionLine: true,
      showChildren: false,
      label: '贴图设定',
      hidden: !mapEnable,
      children: [
        {
          uiType: 'InputSlider',
          fieldName: 'map_saturation',
          label: '饱和度',
          min: 0,
          max: 2,
          tooltip: '调整贴图饱和度（贴图颜色的灰阶与原始颜色的插值，0为颜色的灰阶，1为原始颜色）',
        },
        {
          uiType: 'InputSlider',
          fieldName: 'map_contrast',
          label: '对比度',
          min: 0,
          max: 2,
          tooltip: '调整对比度（中性灰与原始颜色插值，0 为中性灰，1 为原始颜色）',
        },
        {
          uiType: 'InputSlider',
          fieldName: 'map_brightness',
          label: '亮度',
          min: 0,
          max: 2,
          tooltip: '调整亮度（该数值与颜色贴图rgb相乘）',
        },
      ],
    },
    // 金属度和粗糙度
    {
      uiType: 'InputSlider',
      fieldName: 'metalness',
      label: '金属度系数',
      min: 0,
      max: 1,
      tooltip:
        '定义材质是否为金属。金属度值为 0 时为非金属（如木材、塑料），值为 1 时为完全金属（如金属表面）。',
    },
    {
      uiType: 'Select',
      fieldName: 'textureSlots.metalnessMap.value',
      label: '金属度贴图',
      options: (config.textureSlots.metalnessMap as TextureSlotSelect).options,
      allowClear: true,
    },
    {
      uiType: 'InputSlider',
      fieldName: 'roughness',
      label: '粗糙度系数',
      min: 0,
      max: 1,
      tooltip: '控制表面的光滑程度。',
    },
    {
      uiType: 'Select',
      fieldName: 'textureSlots.roughnessMap.value',
      label: '粗糙度贴图',
      options: (config.textureSlots.roughnessMap as TextureSlotSelect).options,
      allowClear: true,
    },
    // 透明
    {
      uiType: 'Group',
      showConnectionLine: true,
      showChildren: false,
      label: '透明',
      children: [
        {
          uiType: 'CheckBox',
          fieldName: 'transparent',
          label: '透明',
        },
        {
          uiType: 'InputSlider',
          fieldName: 'opacity',
          label: '不透明度',
          min: 0,
          max: 1,
        },
        {
          uiType: 'Select',
          fieldName: 'textureSlots.alphaMap.value',
          label: '透明贴图',
          options: (config.textureSlots.alphaMap as TextureSlotSelect).options,
          allowClear: true,
        },
        {
          uiType: 'Select',
          fieldName: 'blend',
          label: '混合',
          options: [
            { key: 0, label: '正常' },
            { key: 1, label: '相加' },
          ],
        },
        {
          uiType: 'InputSlider',
          fieldName: 'alphaTest',
          label: '透明阈值',
          min: 0,
          max: 1,
          tooltip:
            '通过对 Alpha 值进行比较操作剔除低透明度的像素，用于处理具有透明区域的纹理，例如树叶、栅格等复杂模型。',
        },
      ],
    },
    // 自发光
    {
      uiType: 'Group',
      showConnectionLine: true,
      showChildren: false,
      label: '自发光',
      children: [
        {
          uiType: 'ColorPicker',
          fieldName: 'emissive',
          label: '自发光颜色',
          type: ['pure'],
          disableAlpha: true,
          tooltip: '模拟材质表面自发光的颜色，用于模拟如 LED 屏幕、霓虹灯等效果。',
        },
        {
          uiType: 'Select',
          fieldName: 'textureSlots.emissiveMap.value',
          label: '自发光贴图',
          options: (config.textureSlots.emissiveMap as TextureSlotSelect).options,
          allowClear: true,
        },
        {
          uiType: 'InputSlider',
          fieldName: 'emissiveIntensity',
          label: '自发光强度',
          min: 0,
          max: 1,
        },
      ],
    },
    // 法线凹凸置换
    {
      uiType: 'Group',
      showConnectionLine: true,
      showChildren: false,
      label: '法线凹凸置换',
      children: [
        {
          uiType: 'Select',
          fieldName: 'textureSlots.normalMap.value',
          label: '法线贴图',
          options: (config.textureSlots.normalMap as TextureSlotSelect).options,
          allowClear: true,
        },
        {
          uiType: 'InputSlider',
          fieldName: 'normalScale.x',
          label: '法线扰动',
          min: 0,
          max: 2,
          tooltip: '通过改变法线向量来模拟表面细微的凸起和凹陷，使材质表面看起来更复杂和真实。',
        },
        // 凹凸
        {
          uiType: 'InputSlider',
          fieldName: 'bumpScale',
          label: '凹凸强度',
          min: 0,
          max: 1,
        },
        {
          uiType: 'Select',
          fieldName: 'textureSlots.bumpMap.value',
          label: '凹凸贴图',
          options: (config.textureSlots.bumpMap as TextureSlotSelect).options,
          allowClear: true,
        },
        {
          uiType: 'InputSlider',
          fieldName: 'displacementScale',
          label: '置换强度',
          min: 0,
          max: 1,
        },
        {
          uiType: 'Select',
          fieldName: 'textureSlots.displacementMap.value',
          label: '置换贴图',
          options: (config.textureSlots.displacementMap as TextureSlotSelect).options,
          allowClear: true,
        },
        {
          uiType: 'InputSlider',
          fieldName: 'displacementBias',
          label: '置换偏移',
          min: 0,
          max: 1,
        },
      ],
    },
    // AO
    {
      uiType: 'Group',
      showConnectionLine: true,
      showChildren: false,
      label: 'AO',
      children: [
        {
          uiType: 'Select',
          fieldName: 'textureSlots.aoMap.value',
          label: 'AO贴图',
          options: (config.textureSlots.aoMap as TextureSlotSelect).options,
          allowClear: true,
        },
        {
          uiType: 'InputSlider',
          fieldName: 'aoMapIntensity',
          label: 'AO强度',
          min: 0,
          max: 1,
        },
      ],
    },
    // 环境与光照
    {
      uiType: 'Group',
      showConnectionLine: true,
      showChildren: false,
      label: '环境与光照',
      children: [
        {
          uiType: 'Select',
          fieldName: 'textureSlots.envMap.value',
          label: '环境贴图',
          options: (config.textureSlots.envMap as TextureSlotSelect).options,
          allowClear: true,
        },
        {
          uiType: 'Select',
          fieldName: 'textureSlots.lightMap.value',
          label: '光照贴图',
          options: (config.textureSlots.lightMap as TextureSlotSelect).options,
          allowClear: true,
        },
      ],
    },
    // 渲染
    {
      uiType: 'Group',
      showConnectionLine: true,
      showChildren: false,
      label: '渲染',
      children: [
        {
          uiType: 'CheckBox',
          fieldName: 'alphaToCoverage',
          label: '透明多采样',
          tooltip:
            '应用多重采样抗锯齿（MSAA）技术以提高透明材质边缘的渲染质量，特别适合透明度渐变的场景。',
        },
        {
          uiType: 'Select',
          fieldName: 'side',
          label: '渲染面',
          options: [
            { key: 0, label: '正面' },
            { key: 1, label: '背面' },
            { key: 2, label: '双面' },
          ],
          tooltip:
            '确定渲染的几何面向（正面、背面或双面），与三角形法线方向和裁剪面剔除机制紧密相关，影响渲染效率和视觉效果。',
        },
        {
          uiType: 'CheckBox',
          fieldName: 'depthWrite',
          label: '深度写入',
          tooltip:
            '控制渲染对象是否将其深度值写入深度缓冲区，用于定义遮挡关系和优化渲染性能，通常在处理不透明物体时启用。',
        },
        {
          uiType: 'CheckBox',
          fieldName: 'depthTest',
          label: '深度测试',
          tooltip:
            '基于像素深度值与深度缓冲区的比较结果，决定当前像素是否被渲染，用于实现正确的前后遮挡效果。',
        },
        {
          uiType: 'CheckBox',
          fieldName: 'fog',
          label: '雾效',
          tooltip: '决定是否受到场景中雾气的影响。',
        },
      ],
    },
  ];
}
